/**
 * friendlyError — converts raw HTTP status codes / axios errors /
 * fetch Response errors into user-facing, non-technical messages.
 *
 * NEVER show "HTTP 503" or "HTTP 422" to a user again. Route every
 * error through this helper before rendering.
 *
 * Usage:
 *   try { ... } catch (e) { setError(friendlyError(e)); }
 *   Or with a Response: friendlyErrorFromResponse(res)
 */

// Snake_case error codes some backend services return as `detail`. We
// translate them into plain English before showing anything to a real
// human — nobody wants to see `landlord_only` in a red banner.
const CODE_MESSAGES = {
  auth_required:      "Please sign in again — your session expired.",
  landlord_only:      "This is a landlord-only feature. Sign in as a landlord to continue.",
  tenant_only:        "This is a tenant-only feature. Sign in as a tenant to continue.",
  admin_only:         "This is an admin-only feature.",
  not_authenticated:  "Please sign in again — your session expired.",
  invalid_credentials:"Email or password is incorrect. Please try again.",
  email_exists:       "An account with that email already exists. Try signing in.",
  weak_password:      "Password is too weak. Please use at least 6 characters.",
  rate_limited:       "You're going a bit fast — please wait a moment and try again.",
  budget_exhausted:   "The smart features are catching a break — try again shortly.",
  llm_budget_exhausted:"The smart features are catching a break — try again shortly.",
  quota_exhausted:    "Daily limit reached — please try again tomorrow or upgrade for unlimited access.",
  percent_must_be_0_to_100: "Percentage must be between 0 and 100.",
  flat_amount_too_large:    "Flat discount amount is too large — please lower it.",
  lease_not_found:    "We couldn't find that lease.",
  property_not_found: "We couldn't find that property.",
  session_expired:    "Please sign in again — your session expired.",
  no_active_lease:    "You don't have an active lease yet.",
};

function _friendlyForCode(detail) {
  if (typeof detail !== "string") return null;
  const key = detail.trim().toLowerCase();
  if (CODE_MESSAGES[key]) return CODE_MESSAGES[key];
  // Any lowercase snake_case token 3-40 chars looks like a raw error
  // code — rewrite it into human words so the UI doesn't leak internals.
  if (/^[a-z][a-z0-9_]{2,40}$/.test(key) && key.includes("_") && !key.includes(" ")) {
    return key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) + ".";
  }
  return null;
}

const STATUS_MESSAGES = {
  400: "That didn't look right — please double-check the details and try again.",
  401: "Please sign in and try again.",
  402: "This feature is part of the paid plan. Upgrade to unlock it.",
  403: "You don't have access to this action.",
  404: "We couldn't find that.",
  408: "The request timed out — please try again in a moment.",
  409: "That already exists. Please refresh and try again.",
  413: "That file is too large. Please pick a smaller one.",
  422: "Some fields need attention. Please review and try again.",
  429: "Too many attempts. Please wait a minute and try again.",
  500: "Something didn't work on our side. We've been notified — try again in a moment.",
  502: "Service is briefly unavailable. Try again in a moment.",
  503: "This feature is briefly unavailable. Try again in a moment.",
  504: "The request took too long. Please try again in a moment.",
};

function _friendlyForStatus(status) {
  if (!status) return null;
  const n = Number(status);
  if (STATUS_MESSAGES[n]) return STATUS_MESSAGES[n];
  if (n >= 500) return STATUS_MESSAGES[500];
  if (n >= 400) return STATUS_MESSAGES[400];
  return null;
}

/**
 * Accepts an Error, axios error, string, or plain object with `detail`.
 * Returns a plain-English string.
 */
export function friendlyError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  // Axios error shape
  if (err.isAxiosError || err.response) {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    // Prefer a plain-English code translation before showing raw detail.
    const coded = _friendlyForCode(detail);
    if (coded) return coded;
    if (typeof detail === "string" && !/^HTTP\s*\d/i.test(detail)) return detail;
    const friendly = _friendlyForStatus(status);
    if (friendly) return friendly;
  }
  // Fetch-style thrown Error where the message is `HTTP 503`
  const msg = String(err.message || err || "");
  // Raw HTML pages (Cloudflare 5xx, nginx 502) sometimes end up here as
  // the message when someone did `throw new Error(await r.text())`.
  // Detect and replace with a friendly status message so the UI doesn't
  // render <!DOCTYPE html> verbatim.
  if (/^\s*<(!doctype|html|head|body|div)/i.test(msg) || /cloudflare/i.test(msg) || /nginx/i.test(msg)) {
    return "Service is briefly unavailable. Try again in a moment.";
  }
  const httpMatch = msg.match(/^HTTP\s*(\d{3})/i);
  if (httpMatch) {
    const friendly = _friendlyForStatus(httpMatch[1]);
    if (friendly) return friendly;
  }
  // Bare status code lookup
  if (err.status) {
    const friendly = _friendlyForStatus(err.status);
    if (friendly) return friendly;
  }
  // Detail-shaped object
  const codedDetail = _friendlyForCode(err.detail);
  if (codedDetail) return codedDetail;
  if (typeof err.detail === "string" && !/^HTTP\s*\d/i.test(err.detail)) return err.detail;
  // Reasonable-length message that isn't a raw status code
  if (msg && msg.length < 200 && !/^HTTP\s*\d/i.test(msg)) return msg;
  return fallback;
}

/**
 * For fetch() Response objects — reads body if possible, otherwise falls
 * back to the status-based message.
 */
export async function friendlyErrorFromResponse(res, fallback) {
  if (!res) return fallback || STATUS_MESSAGES[500];
  try {
    const j = await res.clone().json();
    const coded = _friendlyForCode(j?.detail);
    if (coded) return coded;
    if (typeof j?.detail === "string" && !/^HTTP\s*\d/i.test(j.detail)) return j.detail;
  } catch (_) { /* not JSON */ }
  return _friendlyForStatus(res.status) || fallback || STATUS_MESSAGES[500];
}
